import { FormsElementTypeEnum, TrainingsContentBlockInput } from 'generated/resources/graphql'

import { nullifyIdIfNew } from 'modules/forms/components/newFormEditor/helpers'
import {
  mapOptions,
  mapOptionsToSave,
  quizBlockToQuestionnaire,
} from 'modules/trainings/components/content/items/quiz/FormQuizHelper'
import { FormElement, QuizBlock } from 'modules/trainings/lib/content/QuizBlock'
import { BlockMapper } from 'modules/trainings/lib/content/mappers/graphql/BlockMapperRegistry'
import { MapperTransformationError } from 'modules/trainings/lib/content/mappers/graphql/errors'
import { GQLContentBlock } from 'modules/trainings/lib/content/mappers/graphql/types'

function isQuizContentable(contentable: GQLContentBlock['contentable']): contentable is NonNullable<
  GQLContentBlock['contentable']
> & {
  __typename: 'TrainingsContentBlockQuiz'
} {
  return contentable?.__typename === 'TrainingsContentBlockQuiz'
}

type FormElementType = {
  id: string
  label: string
  description?: string | null | undefined
  type: FormsElementTypeEnum
  position: number
  mandatory: boolean
  hasCorrectAnswer: boolean
  sectionId?: string
  options: Array<{
    id: string
    label: string
    value: string
    isCorrect: boolean
  }>
}

/**
 * Finds all elements that belong to a specific section
 */
const findSectionElements = (
  allElements: FormElementType[],
  sectionId: string
): FormElementType[] => {
  return allElements.filter((el) => el.sectionId === sectionId)
}

const mapGQLElementBase = (element: FormElementType): FormElement => ({
  id: element.id,
  label: element.label,
  description: element.description || null,
  type: element.type,
  position: element.position,
  mandatory: element.mandatory,
  hasCorrectAnswer: element.hasCorrectAnswer,
  options: mapOptions(element.options),
})

/**
 * Maps a GraphQL element to FormElement, handling sections with nested elements
 * GraphQL returns flat list with sectionId, FormElement uses childrenElements
 */
const mapGQLElementToFormElement = (
  element: FormElementType,
  allElements: FormElementType[]
): FormElement => {
  const baseElement = mapGQLElementBase(element)

  if (element.type === 'section') {
    const childElements = findSectionElements(allElements, element.id)
      .sort((a, b) => a.position - b.position)
      .map(mapGQLElementBase)

    if (childElements.length > 0) {
      return {
        ...baseElement,
        childrenElements: childElements,
      }
    }
  }

  return baseElement
}

export const quizBlockMapper: BlockMapper<QuizBlock> = {
  graphqlTypename: 'TrainingsContentBlockQuiz',
  blockConstructor: QuizBlock,

  fromGraphQL(gqlBlock: GQLContentBlock): QuizBlock {
    const contentable = gqlBlock.contentable

    if (!isQuizContentable(contentable)) {
      throw new MapperTransformationError(
        'TrainingsContentBlockQuiz',
        `Expected TrainingsContentBlockQuiz, got ${contentable?.__typename || 'null'}`,
        gqlBlock
      )
    }
    return new QuizBlock({
      id: gqlBlock.id,
      uuid: gqlBlock.uuid,
      title: gqlBlock.title,
      accesible: gqlBlock.accesible,
      status: gqlBlock.status,
      form: contentable.form
        ? {
            id: contentable.form.id,
            title: contentable.form.title,
            description: contentable.form.description,
            resourceId: contentable.form.resourceId,
            resourceType: contentable.form.resourceType,
            // Only map root elements (without sectionId) - children are nested via sectionId
            elements: contentable.form.elements
              .filter((el) => !el.sectionId || el.sectionId === '')
              .sort((a, b) => a.position - b.position)
              .map((el) => mapGQLElementToFormElement(el, contentable.form?.elements || [])),
            configuration: contentable.form.configuration
              ? {
                  allowedElements: contentable.form.configuration.allowedElements,
                  retryable: contentable.form.configuration.retryable,
                  scorable: contentable.form.configuration.scorable,
                }
              : undefined,
          }
        : undefined,
    })
  },

  toGraphQL(block: QuizBlock): TrainingsContentBlockInput {
    const questionnaire = quizBlockToQuestionnaire(block)

    const mapElementToGQL = (element: (typeof questionnaire.elements)[number]) => ({
      id: nullifyIdIfNew(element.id),
      label: element.label,
      description: element.description,
      type: element.type,
      position: element.position,
      mandatory: element.mandatory,
      hasCorrectAnswer: element.hasCorrectAnswer,
      options: element.options ? mapOptionsToSave(element.options) : null,
    })

    return {
      uuid: block.uuid,
      title: block.title,
      type: 'quiz',
      typeQuiz: {
        form: {
          id: block.form.id,
          title: block.form.title,
          description: block.form.description,
          resourceType: block.form.resourceType,
          resourceId: block.form.resourceId,
          elements: questionnaire.elements.map((element) => ({
            ...mapElementToGQL(element),
            elements:
              element.type === 'section' && 'elements' in element && Array.isArray(element.elements)
                ? element.elements.map(mapElementToGQL)
                : undefined,
          })),
        },
      },
    }
  },
}
